import SlideManager from 'slidemanager'
import Block from './Block'
import TweenMax, { Power4, Expo, TimelineLite } from 'gsap/TweenMax'
import SplitText from '../util/SplitText'

export default class HeroSlider extends Block {
  constructor(el, destroyLast) {
    super(el, destroyLast)

    this.el = el
    this.destroyLast = destroyLast

    this.currentIndex = 0
    this.currentZIndex = 1

    this.splitNumbers(this.numbers)
    this.createSlider()
    this.initSlider()
  }

  bindMethods() {
    super.bindMethods()

    this.nextSlide = this.nextSlide.bind(this)
    this.hideOldSlide = this.hideOldSlide.bind(this)
    this.showNewSlide = this.showNewSlide.bind(this)
    this.showNextSlide = this.showNextSlide.bind(this)
    this.hideNextSlide = this.hideNextSlide.bind(this)
  }

  getElems() {
    super.getElems()
    this.items = []

    this.slideWrapper = this.el.querySelector('.slide-wrapper')
    this.images = this.el.querySelectorAll('.slide-img')
    this.pagerNext = this.el.querySelector('.pager-next')
    this.numbers = this.el.querySelectorAll('.counter__slides-number')
    this.slides = this.el.querySelectorAll('.classic-content')
    this.headings = this.el.querySelectorAll('.heading')

    for (let i = 0; i < this.slides.length; i++) {
      const el = this.slides[i]

      const item = {
        subheading: el.querySelector('.subheading'),
        heading: el.querySelector('.heading'),
        content: el.querySelector('.classic-content__content'),
        cta: el.querySelector('.cta')
      }

      this.items.push(item)
    }
  }

  events() {
    super.events()

    this.pagerNext.addEventListener('click', this.nextSlide)
    this.pagerNext.addEventListener('mouseenter', this.showNextSlide)
    this.pagerNext.addEventListener('mouseleave', this.hideNextSlide)
  }

  splitNumbers(number) {
    if (!number) return

    for (let i = 0; i < number.length; i++) {
      this.splittedNumber = new SplitText(number[i], {
        type: 'chars',
        charsClass: 'number'
      })
    }
  }

  createSlider() {
    this.slider = new SlideManager({
      el: this.slideWrapper,
      loop: true,
      callback: (event) => {
        this.oldIndex = event.previous
        this.currentIndex = event.current
        this.direction = event.direction

        this.hideOldSlide()
          .then(this.showNewSlide)
          .then(() => {
            this.slider.done()
          })
      }
    })
  }

  initSlider() {
    this.images[0].classList.add('active')
    this.slides[0].classList.add('active')

    for (let i = 0; i < this.headings.length; i++) {
      if (i !== 0) {
        const chars = this.headings[i].querySelectorAll('.char > span')

        TweenMax.set(chars, {x: '100%'})
      }
    }
  }

  showHeading(heading) {
    const chars = heading.querySelectorAll('.char > span')

    TweenMax.set(chars, {x: '100%'})
    TweenMax.staggerTo(chars, 0.65, {
      x: '0%',
      ease: Expo.easeOut
    }, 0.03)
  }

  hideHeading(heading) {
    const chars = heading.querySelectorAll('.char > span')

    TweenMax.staggerTo(chars, 0.65, {
      x: '100%',
      ease: Expo.easeIn
    }, 0.03)
  }

  hideOldSlide() {
    return new Promise((resolve) => {
      const item = this.items[this.oldIndex]

      this.hideHeading(item.heading)

      TweenMax.to([item.subheading, item.content, item.cta], 0.75, {
        delay: 0.5,
        alpha: 0,
        ease: Power4.easeOut
      }, 'start')

      resolve()
    })
  }

  showNewSlide() {
    return new Promise((resolve) => {
      const oldSlideNumber = this.numbers[this.oldIndex].querySelectorAll('.number')
      const currentSlideNumber = this.numbers[this.currentIndex].querySelectorAll('.number')

      const item = this.items[this.currentIndex]

      this.isAnimating = true

      const tl = new TimelineLite({
        onStart: () => {
          item.subheading.classList.remove('visible')

          item.subheading.querySelector('.block').style.animation = 'none'

          window.setTimeout(() => {
            item.subheading.querySelector('.block').style.animation = ''
          }, 200)
        },
        onComplete: () => {
          TweenMax.set([this.images[this.oldIndex]], { x: '100%' })
          TweenMax.set(this.images[this.oldIndex].children, {
            x: '-100%',
            scale: 1.1
          })
          TweenMax.set(oldSlideNumber, {
            y: '50%',
            alpha: 0
          })

          this.images[this.oldIndex].classList.remove('active')
          this.images[this.currentIndex].classList.add('active')

          this.slides[this.oldIndex].classList.remove('active')
          this.slides[this.currentIndex].classList.add('active')

          this.isAnimating = false
          resolve()
        }
      })

      tl.addLabel('start', 0)
      tl.to(this.images[this.oldIndex], 1.2, {
        x: '-100%',
        ease: Power4.easeOut
      }, 'start')
      tl.to(this.images[this.oldIndex].children, 1.2, {
        x: '100%',
        ease: Power4.easeOut
      }, 'start')
      tl.to(this.images[this.currentIndex], 1.2, {
        x: '0%',
        ease: Power4.easeOut
      }, 'start')
      tl.to(this.images[this.currentIndex].children, 1.2, {
        x: '0%',
        scale: 1,
        ease: Power4.easeOut
      }, 'start')
      tl.staggerTo(oldSlideNumber, 1.2, {
        y: '-50%',
        alpha: 0,
        ease: Power4.easeOut
      }, 0.05, 'start')
      tl.staggerTo(currentSlideNumber, 1.2, {
        y: '0%',
        alpha: 1,
        ease: Power4.easeOut
      }, 0.1, '-=0.75')
      tl.to([item.subheading, item.content, item.cta], 0.75, {
        alpha: 1,
        ease: Power4.easeOut
      })
      tl.to(item.subheading, 1, {
        css: {
          className: '+=visible'
        }
      }, '-=1')
      tl.add(() => this.showHeading(item.heading), '-=1')

    })
  }

  showNextSlide() {
    // if (this.isAnimating) return

    this.nextIndex = this.currentIndex === this.images.length - 1 ? 0 : this.currentIndex + 1

    this.currentZIndex++
    this.images[this.nextIndex].style.zIndex = this.currentZIndex

    TweenMax.to(this.images[this.nextIndex], 0.5, {
      x: '90%',
      ease: Power4.easeOut
    }, 'start')
    TweenMax.to(this.images[this.nextIndex].children, 0.5, {
      x: '-90%',
      ease: Power4.easeOut
    }, 'start')
  }

  hideNextSlide() {
    // if (this.isAnimating) return

    this.nextIndex = this.currentIndex === this.images.length - 1 ? 0 : this.currentIndex + 1

    TweenMax.to(this.images[this.nextIndex], 0.5, {
      x: '100%',
      ease: Power4.easeOut
    }, 'start')
    TweenMax.to(this.images[this.nextIndex].children, 0.5, {
      x: '-100%',
      ease: Power4.easeOut
    }, 'start')
  }

  // prevSlide() {
  //   this.slider.prev()
  // }

  nextSlide() {
    this.slider.next()
  }
}



// WEBPACK FOOTER //
// ./scripts/blocks/HeroSlider.js